import { createContext } from 'preact';
import { PropsWithChildren } from 'preact/compat';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';

interface ModalContext {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContext | null>(null);

export function useModal() {
  const context = useContext(ModalContext);

  if (context === null) {
    throw new Error(`Webtex Reviews: No modal context was provided.`);
  }

  return context;
}

interface ModalProviderProps {
  ANCHOR_HASH: string;
}

export function ModalProvider({
  ANCHOR_HASH,
  children,
}: PropsWithChildren<ModalProviderProps>) {
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => {
    window.location.hash = ANCHOR_HASH;
    setOpen(true);
  }, [ANCHOR_HASH]);
  const closeModal = useCallback(() => {
    if (window.location.hash === ANCHOR_HASH) {
      location.hash = '';
      history.replaceState('', '', location.pathname);
    }
    setOpen(false);
  }, [ANCHOR_HASH]);

  const contextValue: ModalContext = {
    open,
    openModal,
    closeModal,
  };

  useEffect(() => {
    function openReviewModal() {
      if (window.location.hash === ANCHOR_HASH) {
        openModal();
      }
    }

    openReviewModal();

    function handleHashChange() {
      openReviewModal();
    }

    window.addEventListener('hashchange', handleHashChange);

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [openModal, ANCHOR_HASH]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
}
